import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { useValidationsStore, ValidationStatus } from '@/components/datamodel/hooks/useValidationsStore';
import { BaseValidation } from '@/components/datamodel/types';
import { sanitizeItemForSave, useRemoteValidation } from '@/dialogs/item-editor/ItemEditor.queries';
import { prepareValidationOperations } from '@/dialogs/item-editor/hooks/validation/validation.shared';
import { Item } from '@/types/DataModel';

// Same debounce as the hierarchy CreateNewItemDialog — short enough to
// surface GTIN / mandatory errors while typing, long enough to avoid a
// request per keystroke (LAX-10252).
const VALIDATION_DEBOUNCE_MS = 1500;

const EMPTY_ITEM: Item = { attributeStates__: [] };

const hasErrorLevel = (validations: Record<string, BaseValidation[]>) =>
    Object.values(validations).some((list) =>
        (list || []).some((validation) => validation.level === 'Error' || validation.level === 'Failure'),
    );

const normalizeValidations = (
    validations: Record<string, BaseValidation[]> | undefined,
): Record<string, BaseValidation[]> => {
    if (!validations) {
        return {};
    }

    const normalized: Record<string, BaseValidation[]> = {};
    Object.entries(validations).forEach(([attributeName, list]) => {
        normalized[attributeName] = (list || []).map((validation) => ({
            ...validation,
            path: validation.path?.length ? validation.path : [attributeName],
        }));
    });
    return normalized;
};

/**
 * Remote-validates the attributes currently shown in the create/copy
 * predialog, using the same trigger pattern as the hierarchy
 * CreateNewItemDialog: one request on open / attribute-set change, then a
 * debounced request after the user edits a field.
 *
 * Results land in `useValidationsStore` so the existing AttributeComponents
 * render errors and warnings, and `ItemComplianceIndicator` can show the
 * same header state as the item editor. The store is cleared on unmount so
 * leftovers do not leak into the item editor that opens after the predialog.
 */
export const usePriorItemValidation = (attributeNames: string[]) => {
    const { watch, getValues } = useFormContext();
    const previousItemRef = useRef<Item>(EMPTY_ITEM);
    const isValidationInProgressRef = useRef(false);
    const lastValidatedValuesRef = useRef<Record<string, unknown> | null>(null);
    const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialValidationDoneRef = useRef(false);
    const attributeNamesRef = useRef(attributeNames);
    attributeNamesRef.current = attributeNames;

    const { applyServerValidations, clearErrors, clearWarnings, setStatus } = useValidationsStore(
        useShallow((state) => ({
            applyServerValidations: state.applyServerValidations,
            clearErrors: state.clearErrors,
            clearWarnings: state.clearWarnings,
            setStatus: state.setStatus,
        })),
    );

    const applyResponse = useCallback((
        validations: Record<string, BaseValidation[]> | undefined,
        validatedAttributeNames: string[],
    ) => {
        const normalized = normalizeValidations(validations);
        const operations = prepareValidationOperations({
            validations: normalized,
            validatedAttributeNames,
        });

        // Predialog fields are always top-level. AttributeComponent / Layout
        // look up `validationErrors[attribute.name]`. Server paths can map
        // elsewhere (or be missing), so also index by attributeName — the
        // same key CreateNewItemDialog uses — or the message never appears.
        const indexedByAttributeName = new Set<string>();
        operations.entries.forEach((entry) => {
            if (entry.path === entry.validation.attributeName) {
                indexedByAttributeName.add(entry.validation.attributeName);
            }
        });
        const attributeNameEntries = operations.entries.flatMap((entry) => {
            const attributeName = entry.validation.attributeName;
            if (!attributeName || indexedByAttributeName.has(attributeName)) {
                return [];
            }
            indexedByAttributeName.add(attributeName);
            return [{ ...entry, path: attributeName }];
        });

        applyServerValidations({
            ...operations,
            entries: [...operations.entries, ...attributeNameEntries],
        });
        setStatus(
            hasErrorLevel(normalized)
                ? ValidationStatus.IS_NOT_COMPLIANT
                : ValidationStatus.IS_COMPLIANT,
        );
    }, [applyServerValidations, setStatus]);

    const { mutate: validateItem } = useRemoteValidation({
        onSuccess: (response, variables) => {
            isValidationInProgressRef.current = false;
            previousItemRef.current = { ...variables.newItem };
            lastValidatedValuesRef.current = { ...variables.newItem };
            isInitialValidationDoneRef.current = true;
            applyResponse(
                response.validations,
                variables.attributeNames && variables.attributeNames.length > 0
                    ? variables.attributeNames
                    : attributeNamesRef.current,
            );
        },
        onError: () => {
            isValidationInProgressRef.current = false;
            setStatus(ValidationStatus.IS_FAILED);
        },
    });

    const performValidation = useCallback((
        formValues: Record<string, unknown>,
        isInitialValidation = false,
        names: string[] = attributeNamesRef.current,
    ) => {
        if (isValidationInProgressRef.current) {
            return;
        }

        if (!isInitialValidation && !isInitialValidationDoneRef.current) {
            return;
        }

        if (names.length === 0) {
            return;
        }

        // Only send the fields currently on screen. Leftover values from a
        // previous category must not leak into /validate (LAX-10252).
        const category = typeof formValues.category__ === 'string' ? formValues.category__ : '';
        const newItem = sanitizeItemForSave({
            attributeStates__: [],
            category__: category,
            ...Object.fromEntries(
                names.map((name) => [name, formValues[name] === undefined ? null : formValues[name]]),
            ),
        });

        if (lastValidatedValuesRef.current
            && JSON.stringify(newItem) === JSON.stringify(lastValidatedValuesRef.current)) {
            return;
        }

        isValidationInProgressRef.current = true;
        setStatus(ValidationStatus.IS_RUNNING);

        const previousCategory = previousItemRef.current.category__ ?? '';

        validateItem({
            // Scope the request to the fields on screen so we do not pay for
            // a full-item validation and do not store errors for attributes
            // the predialog never showed.
            attributeNames: names,
            newItem,
            oldItem: sanitizeItemForSave({
                ...previousItemRef.current,
                category__: previousCategory,
            }),
        });
    }, [setStatus, validateItem]);

    const attributeNamesKey = attributeNames.join('\0');

    // Reset + initial validation whenever the displayed attribute set changes
    // (dialog open, category change).
    useEffect(() => {
        previousItemRef.current = EMPTY_ITEM;
        lastValidatedValuesRef.current = null;
        isInitialValidationDoneRef.current = false;
        isValidationInProgressRef.current = false;

        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
            validationTimeoutRef.current = null;
        }

        if (attributeNames.length === 0) {
            applyServerValidations({
                clearAll: true,
                attributeNamesToReset: [],
                entries: [],
            });
            setStatus(ValidationStatus.IS_NOT_ASSIGNED);
            return;
        }

        performValidation(getValues(), true, attributeNames);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributeNamesKey]);

    const watchedValues = watch();
    useEffect(() => {
        if (attributeNames.length === 0 || !isInitialValidationDoneRef.current) {
            return;
        }

        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        validationTimeoutRef.current = setTimeout(() => {
            performValidation(watchedValues, false, attributeNames);
        }, VALIDATION_DEBOUNCE_MS);

        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
                validationTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedValues, attributeNamesKey, performValidation]);

    useEffect(() => {
        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
            clearErrors();
            clearWarnings();
            setStatus(ValidationStatus.IS_NOT_ASSIGNED);
        };
    }, [clearErrors, clearWarnings, setStatus]);
};
